import { useState, useEffect, useRef } from "react";
import "./usercss/myappointments.css";
import "./usercss/UserProfile.css";
import "./usercss/records.css";
import { FiEye, FiDownload, FiXCircle, FiSearch, FiCheckCircle, FiCalendar, FiX, FiFileText, FiChevronLeft, FiChevronRight, FiShield } from "react-icons/fi";
import { appointmentService } from "../../services/appointmentService";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import { createAppointmentSlipPDF } from "../../utils/pdfExport";

const getStatusLabel = (status) => {
  const mapping = {
    'PENDING': 'Pending Approval',
    'CONFIRMED': 'Confirmed',
    'CANCELLING': 'Cancelling...',
    'COMPLETED': 'Completed',
    'CANCELLED_BY_USER': 'Cancelled by You',
    'CANCELLED_BY_PRO': 'Cancelled by Professional'
  };
  return mapping[status] || status;
};

export default function MyAppointments({ appointments = [], onConfirmCancel, onNavigateToHistory, isLoading: isLoadingProp = false }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [selected, setSelected] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const listRef = useRef(null);
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [isApproving, setIsApproving] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Filter appointments based on tab, search, and date
  const filterByCommon = (appt) => {
    const searchLower = searchQuery.toLowerCase();
    const statusLabel = getStatusLabel(appt.status).toLowerCase();
    
    const matchesSearch = !searchQuery || (
      appt.clinicName?.toLowerCase().includes(searchLower) ||
      appt.serviceName?.toLowerCase().includes(searchLower) ||
      appt.reference?.toLowerCase().includes(searchLower) ||
      statusLabel.includes(searchLower)
    );

    const matchesDate = (!startDate || appt.date >= startDate) && 
                        (!endDate || appt.date <= endDate);

    return matchesSearch && matchesDate;
  };

  const upcomingAppointments = appointments.filter(appt => 
    appt.status === "PENDING" &&
    filterByCommon(appt)
  );

  const approvedAppointments = appointments.filter(appt => 
    appt.status === "CONFIRMED" &&
    filterByCommon(appt)
  );

  const acceptedAppointments = appointments.filter(appt => 
    appt.status === "ACCEPTED" &&
    filterByCommon(appt)
  );

  const cancelledAppointments = appointments.filter(appt => 
    ["CANCELLED", "CANCELLED_BY_USER", "CANCELLED_BY_PRO"].includes(appt.status) &&
    filterByCommon(appt)
  );

  const counts = {
    upcoming: appointments.filter(a => a.status === "PENDING" && filterByCommon(a)).length,
    approved: appointments.filter(a => a.status === "CONFIRMED" && filterByCommon(a)).length,
    accepted: appointments.filter(a => a.status === "ACCEPTED" && filterByCommon(a)).length,
    cancelled: appointments.filter(a => ["CANCELLED", "CANCELLED_BY_USER", "CANCELLED_BY_PRO"].includes(a.status) && filterByCommon(a)).length,
  };

  const [statusFilter, setStatusFilter] = useState("all");

  const filteredByStatus = (apptList) => {
    if (statusFilter === "all") return apptList;
    if (statusFilter === "user_cancelled") return apptList.filter(a => a.status === "CANCELLED_BY_USER");
    if (statusFilter === "pro_cancelled") return apptList.filter(a => a.status === "CANCELLED_BY_PRO");
    return apptList;
  };

  const handleDownload = async (appt) => {
    createAppointmentSlipPDF(appt, {
      status: appt.status,
      proName: appt.status === "COMPLETED" || appt.status === "ACCEPTED"
        ? (appt.proName || "Medical Professional")
        : "To Be Assigned",
    });
  };

  const currentAppointments = activeTab === "upcoming" 
    ? upcomingAppointments 
    : activeTab === "approved" 
      ? approvedAppointments
      : activeTab === "accepted"
        ? acceptedAppointments
        : filteredByStatus(cancelledAppointments);
  const totalPages = Math.ceil(currentAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = currentAppointments.slice(startIndex, endIndex);

  // Reset page when tab, search, or date filter changes
  useEffect(() => {
    setCurrentPage(1);
    if (activeTab === "upcoming") {
      setStatusFilter("all");
    }
  }, [activeTab, searchQuery, startDate, endDate]);

  // Track when appointments are loaded
  useEffect(() => {
    if (appointments && appointments.length >= 0) {
      setHasLoaded(true);
    }
  }, [appointments]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  return (
    <div className="my-appointments-container">
      {/* Header with Search */}
      <div className="appointments-header-row">
        <div className="appointments-header-right">
          <div className="appointments-search-container">
            <div className="appointments-search-box">
              <div className="appointments-search-box-inner">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder={t.searchAppointments}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="appointments-date-range-filter">
              <div className="date-range-inputs">
                <div className="appointments-date-box">
                  <FiCalendar className="date-icon" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    title="Start Date"
                  />
                </div>
                <span className="date-separator">to</span>
                <div className="appointments-date-box">
                  <FiCalendar className="date-icon" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    title="End Date"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {currentAppointments.length > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                <span>Showing {startIndex + 1}-{Math.min(endIndex, currentAppointments.length)} of {currentAppointments.length} records</span>
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn pagination-btn--prev"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || totalPages === 1}
                >
                  <FiChevronLeft />
                  Previous
                </button>

                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                    <button
                      key={pageNumber}
                      className={`pagination-number ${currentPage === pageNumber ? 'pagination-number--active' : ''}`}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  className="pagination-btn pagination-btn--next"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 1}
                >
                  Next
                  <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status filter tabs (sidebar style) + Appointments List */}
      <div className="appointments-workspace">
        {/* Status Filter Sidebar */}
        <aside className="appointments-sidebar">
          <ul>
            <li
              className={activeTab === "upcoming" ? "active" : ""}
              onClick={() => handleTabClick("upcoming")}
            >
              {t.upcoming} <span className="sidebar-counter">{counts.upcoming}</span>
            </li>
            <li
              className={activeTab === "approved" ? "active" : ""}
              onClick={() => handleTabClick("approved")}
            >
              Approved <span className="sidebar-counter">{counts.approved}</span>
            </li>
            <li
              className={activeTab === "accepted" ? "active" : ""}
              onClick={() => handleTabClick("accepted")}
            >
              Assigned <span className="sidebar-counter">{counts.accepted}</span>
            </li>
            <li
              className={activeTab === "cancelled" ? "active" : ""}
              onClick={() => handleTabClick("cancelled")}
            >
              {t.cancelled} <span className="sidebar-counter">{counts.cancelled}</span>
            </li>
          </ul>
        </aside>

        {/* Appointments List */}
        <div className="appointments-main-workspace">
          <div className="appointments-list" ref={listRef}>
        {isLoadingProp && !hasLoaded ? (
          <div className="appointments-skeleton-container">
            {[...Array(5)].map((_, index) => (
              <div className="appointment-card-skeleton" key={index}>
                <div className="skeleton skeleton-appt-clinic"></div>
                <div className="skeleton skeleton-appt-service"></div>
                <div className="skeleton skeleton-appt-datetime"></div>
                <div className="skeleton skeleton-appt-ref"></div>
                <div className="skeleton skeleton-appt-status"></div>
              </div>
            ))}
          </div>
        ) : paginatedAppointments.length > 0 ? (
          paginatedAppointments.map((appt) => (
            <div key={appt.id} className="appointment-card">
              <span className={`status-badge ${appt.status.toLowerCase()}`}>
                {getStatusLabel(appt.status)}
              </span>
              <div className="appointment-header">
                <div>
                  <div className="appointment-clinic">{appt.clinicName}</div>
                  <div className="appointment-service">{appt.serviceName}</div>
                  <div className="appointment-datetime">
                    {appt.date} • {appt.time}
                  </div>
                  {appt.status === "ACCEPTED" && appt.proName && (
                    <div className="appointment-professional">
                      Medical Professional: {appt.proName}
                    </div>
                  )}
                  <div className="appointment-ref-row">
                    <div className="appointment-ref">
                      Reference #: {appt.reference}
                    </div>
                    <div className="appointment-actions-inline">
                      <button
                        className="action-btn view"
                        onClick={() => {
                          setSelected(appt);
                          setShowDetails(true);
                        }}
                      >
                        <FiEye /> View
                      </button>
                      {appt.status === "CONFIRMED" && (
                        <button
                          className="action-btn download"
                          onClick={() => handleDownload(appt)}
                        >
                          <img
                            src="https://www.svgrepo.com/show/470808/download-alt.svg"
                            alt=""
                            width="12"
                            height="12"
                            style={{ marginRight: '6px' }}
                          />
                          Download
                        </button>
                      )}

                      {['PENDING', 'CONFIRMED'].includes(appt.status) && (
                        <button
                          className="action-btn cancel"
                          onClick={() => {
                            if (onConfirmCancel) onConfirmCancel(appt);
                          }}
                        >
                          <FiXCircle /> Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {["CANCELLED", "CANCELLED_BY_USER", "CANCELLED_BY_PRO"].includes(appt.status) && appt.cancelReason && (
                  <div className="appointment-cancel-reason">
                    Cancellation Reason: {appt.cancelReason}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="no-appointments">
            <p>No appointments found</p>
          </div>
        )}
        </div>
      </div>
      </div>

      {/* DETAILS MODAL */}
      {showDetails && selected && (
        <div className="records-modal-backdrop">
          <div className="records-modal-card">
            <button
              className="records-modal-close"
              onClick={() => setShowDetails(false)}
            >
              <FiX />
            </button>

            {/* Appointment Details at Top */}
            <div className="records-appointment-details-card">
              <div className="records-appointment-details-header">
                <div className="records-appointment-details-icon">
                  <FiCalendar size={20} color="#ffffff" />
                </div>
                <div>
                  <h3 className="records-appointment-details-title">Appointment Details</h3>
                </div>
              </div>
              <div className="records-appointment-details-body">
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Title</span>
                  <span className="records-appointment-detail-value">{selected.serviceName}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Clinic</span>
                  <span className="records-appointment-detail-value">{selected.clinicName}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Reference #</span>
                  <span className="records-appointment-detail-value">{selected.reference || "N/A"}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Category</span>
                  <span className="records-appointment-detail-value">{selected.serviceName}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Date & Time</span>
                  <span className="records-appointment-detail-value">
                    {selected.date} • {selected.time}
                  </span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Professional</span>
                  <span className="records-appointment-detail-value">{selected.proName || "To Be Assigned"}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Status</span>
                  <span className="records-appointment-detail-value">
                    <span className={`status-badge ${selected.status.toLowerCase()}`}>
                      {getStatusLabel(selected.status)}
                    </span>
                  </span>
                </div>
                {selected.cancelReason && (
                  <div className="records-appointment-detail-item">
                    <span className="records-appointment-detail-label">Cancellation Reason</span>
                    <span className="records-appointment-detail-value">{selected.cancelReason}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tabbed Interface */}
            <div className="records-tabs-container">
              <button
                className={`records-tab active`}
              >
                <FiFileText size={16} />
                Consultation
              </button>
            </div>

            {/* Tab Content */}
            <div className="records-tab-content">
              <div className="consultation-content">
                {/* Consultation Information */}
                <div className="consultation-card">
                  <div className="consultation-card-header">
                    <FiShield size={18} color="#3b82f6" />
                    <h4>Consultation Information</h4>
                  </div>
                  <div className="consultation-card-body">
                    <p className="consultation-info">
                      This appointment is scheduled for {selected.serviceName} at {selected.clinicName}.
                      {selected.proName && (
                        <> with {selected.proName}.</>
                      )}
                      For any questions about this appointment, please contact the clinic.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

